import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ButtonVariants, ButtonSizes } from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { useTranslations, useAssistantFilters } from '@/hooks';
import {
  AddAttachmentModal,
  DiscrepancyModal,
  SurplusWriteoffModal,
  SURPLUS_WRITEOFF_MODES,
} from '@/components/common';
import { ATTACHMENT_TARGETS } from '@/lib/api/attachmentsApi';
import { PageHeader } from '@/components/layout';
import { ConfirmDialog } from '@/components/feedback';
import { DeclarationsTable } from '../components/DeclarationsTable';
import { ROUTES, MODULE_CERTIFICATE_TYPES } from '@/app/routes';
import { useUpdateSurplusWriteoffMutation } from '@/lib/api/surplusWriteoffApi';
import {
  useRetryDeclarationExtractionMutation,
  useDeleteDeclarationMutation,
} from '../api/declarationsApi';
import {
  declarationStatusTone,
  buildRowActions,
  buildRowMenuActions,
  buildEmptyState,
} from '../constants/declarationsColumns';
import styles from './DeclarationsPage.module.scss';

// Inbounds ▸ Incoming Declarations list, fed by GET /:module/inbound.
export const DeclarationsPage = () => {
  const t = useTranslations();
  const assistantFilters = useAssistantFilters();
  const navigate = useNavigate();
  const { module } = useParams();

  const [isGenuinelyEmpty, setIsGenuinelyEmpty] = useState(false);
  const [discrepancy, setDiscrepancy] = useState(null);
  const discrepancyRow = discrepancy?.row ?? null;
  const [surplusWriteoffRow, setSurplusWriteoffRow] = useState(null);
  const [retryDeclarationExtraction] = useRetryDeclarationExtractionMutation();
  const [updateSurplusWriteoff, { isLoading: isSavingSurplusWriteoff }] =
    useUpdateSurplusWriteoffMutation();

  const handleAddDeclaration = useCallback(
    () => navigate(ROUTES.declarationCreate(module)),
    [navigate, module],
  );
  const emptyState = useMemo(
    () => buildEmptyState(t, { onAdd: handleAddDeclaration }),
    [t, handleAddDeclaration],
  );
  const handleView = useCallback(
    (row) => navigate(ROUTES.declarationDetail(module, row.id)),
    [navigate, module],
  );

  const handleViewFile = useCallback((row) => {
    if (row.declarationFileUrl) window.open(row.declarationFileUrl, '_blank');
  }, []);
  // Re-queues extraction; the invalidated list brings the row back with its new status.
  const handleRetry = useCallback(
    (row) => retryDeclarationExtraction({ module, id: row.id }),
    [retryDeclarationExtraction, module],
  );

  const handleValidate = useCallback(
    (row) => navigate(ROUTES.validateDeclaration(module, row.id)),
    [navigate, module],
  );
  const handleFulfill = useCallback(() => {
    // TODO: open the fulfill flow once it exists.
  }, []);
  const [attachmentRow, setAttachmentRow] = useState(null);
  const handleAddAttachment = useCallback((row) => setAttachmentRow(row), []);
  const handleCloseAttachment = useCallback(() => setAttachmentRow(null), []);
  const handleEditDeclaration = useCallback(
    (row) => navigate(ROUTES.declarationEdit(module, row.id)),
    [navigate, module],
  );
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteDeclaration, { isLoading: isDeleting }] = useDeleteDeclarationMutation();
  const handleDeleteDeclaration = useCallback((row) => setDeleteRow(row), []);
  const handleCancelDelete = useCallback(() => setDeleteRow(null), []);
  const handleConfirmDelete = useCallback(async () => {
    if (isDeleting) return;
    const result = await deleteDeclaration({ module, id: deleteRow.id });
    if (!result.error) setDeleteRow(null);
  }, [isDeleting, deleteDeclaration, module, deleteRow]);
  const handleSurplusWriteOff = useCallback((row) => setSurplusWriteoffRow(row), []);
  const handleOpenDiscrepancy = useCallback(
    (row, unitType) => setDiscrepancy({ row, unitType }),
    [],
  );
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancy(null), []);
  const handleCloseSurplusWriteoff = useCallback(() => setSurplusWriteoffRow(null), []);
  const handleSubmitSurplusWriteoff = useCallback(
    async (values) => {
      try {
        await updateSurplusWriteoff({ module, id: surplusWriteoffRow.id, ...values }).unwrap();
        setSurplusWriteoffRow(null);
      } catch {
        // notifyApiErrors already surfaced it.
      }
    },
    [updateSurplusWriteoff, module, surplusWriteoffRow],
  );

  // TODO: re-enable once the declaration ⇄ shipment link exists server-side.
  const bulkActions = useMemo(
    () => [{ id: 'link-shipments', label: t('ACTION_LINK_SHIPMENTS'), disabled: true }],
    [t],
  );

  const rowActions = useMemo(
    () =>
      buildRowActions(t, {
        onValidate: handleValidate,
        onRetry: handleRetry,
        onFulfill: handleFulfill,
      }),
    [t, handleValidate, handleRetry, handleFulfill],
  );

  const rowMenuActions = useMemo(
    () =>
      buildRowMenuActions(t, {
        onAddAttachment: handleAddAttachment,
        onEdit: handleEditDeclaration,
        onDelete: handleDeleteDeclaration,
        onSurplusWriteOff: handleSurplusWriteOff,
      }),
    [t, handleAddAttachment, handleEditDeclaration, handleDeleteDeclaration, handleSurplusWriteOff],
  );

  const surplusWriteoffMode = surplusWriteoffRow?.hasSurplusOrWriteOff
    ? SURPLUS_WRITEOFF_MODES.edit
    : SURPLUS_WRITEOFF_MODES.add;

  const discrepancySections = useMemo(() => {
    if (!discrepancyRow) return null;
    const unitType = discrepancy?.unitType ?? discrepancyRow.unitType;
    const view = discrepancyRow.byUnit[unitType] ?? discrepancyRow.byUnit.working;
    return [
      {
        title: t('SECTION_GENERAL_DETAILS'),
        fields: [
          { label: t('FIELD_MATERIAL_NAME'), value: discrepancyRow.materialName },
          {
            label: t('FIELD_STATUS'),
            value: discrepancyRow.status,
            type: 'status',
            tone: declarationStatusTone(discrepancyRow.statusValue),
          },
          { label: t('FIELD_SENT_BY'), value: discrepancyRow.supplierName },
          { label: t('FIELD_TERMINAL'), value: discrepancyRow.terminal },
          { label: t('FIELD_RECEIVED_AT'), value: discrepancyRow.facilityName },
        ],
      },
      {
        title: t('SECTION_QUANTITY'),
        fields: [
          { label: t('FIELD_RECEIVED_DECLARATION_QUANTITY'), value: view.receivedQty },
          { label: t('DECLARATIONS_COL_ADJUSTED_QTY'), value: view.adjustedQty },
        ],
      },
    ];
  }, [discrepancyRow, discrepancy, t]);

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('DECLARATIONS_LIST_TITLE')}
        actions={
          isGenuinelyEmpty ? null : (
            <Button
              variant={ButtonVariants.primary}
              size={ButtonSizes.sm}
              leftIcon={<Plus />}
              onClick={handleAddDeclaration}
            >
              {t('ACTION_ADD_DECLARATIONS')}
            </Button>
          )
        }
      />

      <DeclarationsTable
        extraFilters={assistantFilters}
        certificateType={MODULE_CERTIFICATE_TYPES[module]}
        emptyState={emptyState}
        onView={handleView}
        onViewFile={handleViewFile}
        onOpenDiscrepancy={handleOpenDiscrepancy}
        onEmptyChange={setIsGenuinelyEmpty}
        rowActions={rowActions}
        rowMenuActions={rowMenuActions}
        bulkActions={bulkActions}
      />

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={discrepancyRow?.batchId}
        discrepancies={discrepancyRow?.discrepancies}
        sections={discrepancySections}
      />

      <SurplusWriteoffModal
        open={surplusWriteoffRow != null}
        onClose={handleCloseSurplusWriteoff}
        mode={surplusWriteoffMode}
        row={surplusWriteoffRow}
        onSubmit={handleSubmitSurplusWriteoff}
        isSubmitting={isSavingSurplusWriteoff}
      />

      <AddAttachmentModal
        open={attachmentRow != null}
        onClose={handleCloseAttachment}
        title={t('DECLARATIONS_ADD_ATTACHMENT_MODAL_TITLE', {
          referenceId: attachmentRow?.batchId,
        })}
        target={ATTACHMENT_TARGETS.declaration}
        module={module}
        entityId={attachmentRow?.id}
      />

      <ConfirmDialog
        open={deleteRow != null}
        title={t('DECLARATIONS_DELETE_TITLE')}
        bodyTitle={t('DECLARATIONS_DELETE_BODY_TITLE', { referenceId: deleteRow?.batchId })}
        description={t('DECLARATIONS_DELETE_DESCRIPTION')}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
